import { useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft } from 'lucide-react';
import { deletePricingStrategy, updatePricingStrategy } from '@/api/generated/sdk.gen';
import { strategyQuery, type PricingStrategy } from '@/lib/pricing';
import { messageFromError } from '@/lib/api';
import { useConfirm } from '@/components/DialogProvider';
import { usePageHeader } from '@/components/PageHeader';
import { useToast } from '@/components/ToastProvider';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { RulesEditor } from '@/components/pricing/RulesEditor';

export const Route = createFileRoute('/_app/pricing-strategies/$id')({
  loader: async ({ context, params }) => {
    try {
      await context.queryClient.ensureQueryData(strategyQuery(params.id));
    } catch (err) {
      const status = (err as { status?: number })?.status;
      if (status === 401 || status === 404) return;
      throw err;
    }
  },
  component: StrategyEditorPage,
});

function StrategyEditorPage() {
  const { id } = Route.useParams();
  const { data, isLoading, error } = useQuery(strategyQuery(id));
  usePageHeader({ subtitle: data?.name });

  if (isLoading) {
    return <p className="text-sm text-muted">Loading strategy…</p>;
  }
  if (error || !data) {
    return (
      <div>
        <BackLink />
        <p role="alert" className="mt-4 text-sm text-destructive">
          {error ? messageFromError(error) : 'Strategy not found.'}
        </p>
      </div>
    );
  }
  return <StrategyEditor strategy={data} />;
}

function BackLink() {
  return (
    <Link
      to="/pricing-strategies"
      className="inline-flex items-center gap-1 text-sm text-muted hover:text-foreground"
    >
      <ChevronLeft className="h-4 w-4" />
      All strategies
    </Link>
  );
}

export function StrategyEditor({ strategy }: { strategy: PricingStrategy }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const navigate = useNavigate();
  const askConfirm = useConfirm();

  const deleteMut = useMutation({
    mutationFn: async () => deletePricingStrategy({ throwOnError: true, path: { id: strategy.id } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pricing-strategies'] });
      toast({ kind: 'success', title: 'Strategy deleted', message: strategy.name });
      navigate({ to: '/pricing-strategies' });
    },
    onError: (err) => {
      toast({ kind: 'error', title: 'Could not delete strategy', message: messageFromError(err) });
    },
  });

  return (
    <div>
      <BackLink />

      <header className="mb-6 mt-3 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="truncate font-serif text-2xl font-bold text-[var(--text-heading)]">
            {strategy.name}
          </h1>
          <div className="mt-1">
            {strategy.enabled ? (
              <Badge variant="success" dot>
                Active
              </Badge>
            ) : (
              <Badge variant="neutral" dot>
                Disabled
              </Badge>
            )}
          </div>
        </div>
        <Button
          variant="destructive"
          size="sm"
          loading={deleteMut.isPending}
          onClick={async () => {
            const ok = await askConfirm({
              title: 'Delete strategy?',
              body: `“${strategy.name}” will be removed. Apartments still assigned to it block deletion.`,
              destructive: true,
              confirmLabel: 'Delete',
            });
            if (ok) deleteMut.mutate();
          }}
        >
          Delete
        </Button>
      </header>

      <DetailsSection strategy={strategy} />
      {/* key on id so navigating to a different strategy remounts the editor and
          re-seeds the (seed-once) rule forms, never PUTting stale rules. */}
      <RulesEditor key={strategy.id} strategy={strategy} />
    </div>
  );
}

function DetailsSection({ strategy }: { strategy: PricingStrategy }) {
  const [editing, setEditing] = useState(false);
  return (
    <section className="rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card p-5">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-xs font-semibold uppercase tracking-[0.06em] text-muted">Details</h2>
        {!editing && (
          <Button type="button" variant="secondary" size="sm" onClick={() => setEditing(true)}>
            Edit
          </Button>
        )}
      </div>
      {editing ? (
        <DetailsEditForm
          strategy={strategy}
          onCancel={() => setEditing(false)}
          onSaved={() => setEditing(false)}
        />
      ) : (
        <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm md:grid-cols-3">
          <Field label="Name" value={strategy.name} />
          <Field
            label="Default min-stay"
            value={`${strategy.default_min_stay} ${strategy.default_min_stay === 1 ? 'night' : 'nights'}`}
          />
          <Field label="Rules" value={String(strategy.rules.length)} />
        </dl>
      )}
    </section>
  );
}

function DetailsEditForm({
  strategy,
  onCancel,
  onSaved,
}: {
  strategy: PricingStrategy;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [name, setName] = useState(strategy.name);
  const [minStay, setMinStay] = useState(String(strategy.default_min_stay));
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: async () => {
      const trimmed = name.trim();
      if (!trimmed) throw new Error('Name is required.');
      const ms = Number.parseInt(minStay, 10);
      if (!Number.isFinite(ms) || ms < 1) throw new Error('Default min-stay must be at least 1 night.');
      // PUT replaces the whole strategy, so carry the existing rules through
      // unchanged — this form only edits name + default_min_stay.
      return updatePricingStrategy({
        throwOnError: true,
        path: { id: strategy.id },
        body: { name: trimmed, default_min_stay: ms, rules: strategy.rules },
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pricing-strategies'] });
      toast({ kind: 'success', title: 'Strategy updated', message: name.trim() });
      onSaved();
    },
    onError: (err) => setError(messageFromError(err)),
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        setError(null);
        mut.mutate();
      }}
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} required />
        <Input
          label="Default min-stay (nights)"
          type="number"
          min={1}
          value={minStay}
          onChange={(e) => setMinStay(e.target.value)}
          required
        />
      </div>
      {error && (
        <p role="alert" className="mt-4 text-sm text-destructive">
          {error}
        </p>
      )}
      <div className="mt-5 flex items-center gap-2">
        <Button type="submit" variant="primary" loading={mut.isPending} disabled={mut.isPending}>
          Save
        </Button>
        <Button type="button" variant="secondary" onClick={onCancel} disabled={mut.isPending}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-[0.06em] text-muted">{label}</dt>
      <dd className="mt-0.5">{value}</dd>
    </div>
  );
}
